/** Right-side drawer with one subscription's full local state, the bound
 *  API credential, the persisted registration snapshot, and an on-demand
 *  fetch of the LIVE vendor-side state with a field-by-field diff.
 *
 *  Local vs remote are deliberately separate sections: the local block is
 *  what our database believes, the remote block is what the vendor answered
 *  moments ago (refreshable; 502 = vendor API error). `callback_url` is
 *  highlighted when it drifted from `callback_url_expected` (base-URL change
 *  or an override) — domain sync reconciles that.
 *
 *  Manage actions (retry / enable / disable / mode / delete) are gated by
 *  `console_restech_manage`; the backend re-checks.
 *
 *  When the vendor has legacy webhooks this subscription replaces
 *  (`legacy_webhooks_supported` — Ventrata `octo/webhooks` for BOOKING_UPDATE
 *  and ORDER_UPDATE),
 *  the drawer lists them and the mode button couples the switch with them:
 *  "Switch to process & disable webhooks" (switch, then delete them at the
 *  vendor — a copy is kept) and "Switch to log only & enable webhooks"
 *  (re-create them, then switch; the mode stays if any restore fails).
 */
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as subscriptionsApi from '@/api/notificationSubscriptions';
import type {
  LegacyWebhookOperation,
  LegacyWebhookResult,
  SubscriptionMode,
} from '@/api/notificationSubscriptions';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { HistoryPanel } from '@/components/History/HistoryPanel';
import { useSnackbar } from '@/components/Snackbar';
import { act, ValueActions } from '@/components/ValueActions/ValueActions';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDate } from '@/utils/format';

import {
  accountActions,
  createdActions,
  credentialActions,
  credentialFingerprintActions,
  credentialPrefixActions,
  credentialStatusActions,
  lastErrorActions,
  lastReceivedActions,
  modeActions,
  remoteIdActions,
  restechActions,
  statusActions,
  subscriptionIdActions,
  tenantActions,
  typeActions,
  type SubscriptionCtx,
} from './subscriptionLinks';
import {
  LegacyWebhookOutcome,
  LegacyWebhooksSection,
  legacyWebhookCounts,
} from './LegacyWebhooksSection';
import { LegacyWebhookSwitchDialog, type LegacyWebhookAction } from './LegacyWebhookSwitchDialog';
import { ReconcileFlagSection } from './ReconcileFlagSection';
import { AccountChip, ModeChip, StatusChip } from './subscriptionFormat';

const mono = { fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' } as const;

const vendorLabel = (restech: string) =>
  restech ? restech.charAt(0).toUpperCase() + restech.slice(1) : 'The vendor';

const plural = (n: number) => `${n} webhook${n === 1 ? '' : 's'}`;

interface LegacyOutcome {
  severity: 'warning' | 'error';
  message: string;
  result: LegacyWebhookResult | null;
}

interface Props {
  subscriptionId: number;
  open: boolean;
  onClose: () => void;
  /** Invalidate the grid after a mutation. */
  onChanged?: () => void;
}

export function SubscriptionDetailDrawer({ subscriptionId, open, onClose, onChanged }: Props) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_restech_manage');

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmProcess, setConfirmProcess] = useState(false);
  const [deleteConflict, setDeleteConflict] = useState<string | null>(null);
  const [legacyAction, setLegacyAction] = useState<LegacyWebhookAction | null>(null);
  const [legacyOutcome, setLegacyOutcome] = useState<LegacyOutcome | null>(null);

  const detailQuery = useQuery({
    queryKey: ['subscription', subscriptionId],
    queryFn: () => subscriptionsApi.retrieve(subscriptionId),
    enabled: open,
  });

  // Live vendor state — fetched when the drawer opens, refreshable on demand.
  const remoteQuery = useQuery({
    queryKey: ['subscription-remote', subscriptionId],
    queryFn: () => subscriptionsApi.remote(subscriptionId),
    enabled: open,
    retry: false,
    staleTime: 0,
  });

  // Legacy vendor webhooks — the mode buttons depend on them, so the drawer
  // owns the query (LegacyWebhooksSection only renders it).
  const legacySupported = Boolean(detailQuery.data?.legacy_webhooks_supported);
  const legacyQuery = useQuery({
    queryKey: ['subscription-legacy-webhooks', subscriptionId],
    queryFn: () => subscriptionsApi.legacyWebhooks(subscriptionId),
    enabled: open && legacySupported,
    retry: false,
    staleTime: 0,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['subscription', subscriptionId] });
    void qc.invalidateQueries({ queryKey: ['subscriptions'] });
    void qc.invalidateQueries({ queryKey: ['subscription-legacy-webhooks', subscriptionId] });
    onChanged?.();
  };
  // Config-only changes (reconcile flag): no reason to re-list the vendor's webhooks.
  const invalidateDetail = () => {
    void qc.invalidateQueries({ queryKey: ['subscription', subscriptionId] });
    void qc.invalidateQueries({ queryKey: ['subscriptions'] });
    onChanged?.();
  };

  /** 200 answers: a partially failed run still answers 200 with `ok` false. */
  const reportLegacy = (res: LegacyWebhookOperation, success: string, partial: string) => {
    const r = res.webhooks;
    if (!r || r.ok) {
      setLegacyOutcome(null);
      snackbar.show(success, 'success');
    } else {
      setLegacyOutcome({ severity: 'warning', message: partial, result: r });
      snackbar.show(partial, 'warning');
    }
  };

  /** 502 = a restore failed and the mode is unchanged (body carries the run). */
  const reportLegacyError = (e: unknown, fallback: string) => {
    const body =
      e instanceof ConsoleApiError
        ? (e.body as Partial<LegacyWebhookOperation> | undefined)
        : undefined;
    if (e instanceof ConsoleApiError && e.status === 502 && body?.webhooks !== undefined) {
      setLegacyOutcome({
        severity: 'error',
        message:
          'Mode not changed: not every webhook could be restored, so the subscription stays in ' +
          'process mode and updates keep arriving. Restored webhooks are live again.',
        result: body.webhooks ?? null,
      });
    } else {
      snackbar.show(e instanceof ConsoleApiError ? e.message : fallback, 'error');
    }
  };

  const switchMutation = useMutation({
    mutationFn: (mode: SubscriptionMode) =>
      subscriptionsApi.switchMode(subscriptionId, { mode, legacy_webhooks: true }),
    onSuccess: (res, mode) => {
      const n = res.webhooks?.webhooks.filter((w) => w.result === 'ok').length ?? 0;
      if (mode === 'process') {
        reportLegacy(
          res,
          `Process mode on; ${plural(n)} disabled.`,
          'Process mode is on, but not every webhook was disabled — the ones listed are still ' +
            'live (double-processing is harmless). Retry with "Disable webhooks".',
        );
      } else {
        reportLegacy(res, `Log-only mode on; ${plural(n)} restored.`, 'Log-only mode on.');
      }
      invalidate();
    },
    onError: (e: unknown) => {
      reportLegacyError(e, 'Switch failed.');
      invalidate();
    },
  });

  const disableWebhooksMutation = useMutation({
    mutationFn: () => subscriptionsApi.disableLegacyWebhooks(subscriptionId),
    onSuccess: (res) => {
      const n = res.webhooks?.webhooks.filter((w) => w.result === 'ok').length ?? 0;
      reportLegacy(
        res,
        `${plural(n)} disabled.`,
        'Not every webhook was disabled — the ones listed are still live.',
      );
      invalidate();
    },
    onError: (e: unknown) => {
      reportLegacyError(e, 'Disabling the webhooks failed.');
      invalidate();
    },
  });

  const retryMutation = useMutation({
    mutationFn: () => subscriptionsApi.retry(subscriptionId),
    onSuccess: (row) => {
      snackbar.show(
        row.status === 'active'
          ? 'Registered with the vendor — subscription is active.'
          : 'Retry ran, but the vendor registration failed again.',
        row.status === 'active' ? 'success' : 'warning',
      );
      invalidate();
      void remoteQuery.refetch();
    },
    onError: (e: unknown) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Retry failed.', 'error'),
  });

  const updateMutation = useMutation({
    mutationFn: (body: subscriptionsApi.SubscriptionUpdate) =>
      subscriptionsApi.update(subscriptionId, body),
    onSuccess: () => {
      snackbar.show('Subscription updated.', 'success');
      invalidate();
    },
    onError: (e: unknown) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Update failed.', 'error'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => subscriptionsApi.remove(subscriptionId),
    onSuccess: () => {
      snackbar.show('Subscription deleted (row, credential, remote registration).', 'success');
      invalidate();
      onClose();
    },
    onError: (e: unknown) => {
      if (e instanceof ConsoleApiError && e.status === 409) {
        // The vendor-side delete failed: the row was KEPT as delete_failed
        // (with its credential, so deliveries still drop with a 200).
        setDeleteConflict(e.message);
        invalidate();
      } else {
        snackbar.show(e instanceof ConsoleApiError ? e.message : 'Delete failed.', 'error');
      }
    },
  });

  const s = detailQuery.data;
  const ctx: SubscriptionCtx = { tenantId: s?.tenant?.id ?? null, restech: s?.restech ?? '' };
  const remote = remoteQuery.data;
  const remoteError =
    remoteQuery.error instanceof ConsoleApiError ? remoteQuery.error : null;

  const urlDrifted = Boolean(
    s?.callback_url_expected && s.callback_url !== s.callback_url_expected,
  );
  const canRetry = s?.status === 'pending' || s?.status === 'create_failed';
  const canToggleEnabled = s?.status === 'active' || s?.status === 'disabled';
  const setMode = (mode: SubscriptionMode) => {
    if (mode === 'process') setConfirmProcess(true);
    else updateMutation.mutate({ mode });
  };

  const vendor = vendorLabel(s?.restech ?? '');
  const legacy = legacyWebhookCounts(legacyQuery.data);
  // Never offer a plain switch before we know whether webhooks are involved.
  const legacyLoading = legacySupported && legacyQuery.isPending;
  const modeBusy =
    updateMutation.isPending || switchMutation.isPending || disableWebhooksMutation.isPending;
  const forwardWithWebhooks =
    s?.mode === 'log_only' && s.status === 'active' && legacy.disableable.length > 0;
  const reverseWithWebhooks = s?.mode === 'process' && legacy.disabled.length > 0;
  const canDisableWebhooks =
    s?.mode === 'process' && s.status === 'active' && legacy.disableable.length > 0;
  const modeLabel = forwardWithWebhooks
    ? 'Switch to process & disable webhooks'
    : reverseWithWebhooks
      ? 'Switch to log only & enable webhooks'
      : s?.mode === 'log_only'
        ? 'Switch to process'
        : 'Switch to log only';
  const onModeClick = () => {
    if (!s) return;
    if (forwardWithWebhooks) setLegacyAction('process');
    else if (reverseWithWebhooks) setLegacyAction('log_only');
    else setMode(s.mode === 'log_only' ? 'process' : 'log_only');
  };

  const drawer = (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // The AppBar sits at zIndex.drawer+1; temporary drawers default to
      // zIndex.drawer, so without this the app bar covers the drawer top.
      sx={{ zIndex: (t) => t.zIndex.modal }}
    >
      <Box sx={{ width: 520, p: 2 }} role="presentation">
        <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Subscription{' '}
            <ValueActions actions={subscriptionIdActions(subscriptionId, ctx)}>
              #{subscriptionId}
            </ValueActions>
          </Typography>
          {s && (
            <ValueActions chip actions={statusActions(s.status, ctx)}>
              <StatusChip status={s.status} />
            </ValueActions>
          )}
          <IconButton onClick={onClose} size="small" sx={{ ml: 1 }} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>

        {detailQuery.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {detailQuery.isError && <Alert severity="error">Couldn’t load the subscription.</Alert>}

        {deleteConflict && (
          <Alert severity="error" onClose={() => setDeleteConflict(null)} sx={{ mb: 2 }}>
            The vendor-side delete failed, so the row was kept as <b>delete_failed</b> — its
            credential stays so incoming deliveries still drop with a 200. Retry the delete
            later. ({deleteConflict})
          </Alert>
        )}

        {s && (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={3}>
              <Field label="Tenant">
                {s.tenant ? (
                  <ValueActions actions={tenantActions(s.tenant)}>
                    {`${s.tenant.name} (${s.tenant.code})`}
                  </ValueActions>
                ) : (
                  '—'
                )}
              </Field>
              <Field label="Restech">
                <ValueActions actions={restechActions(ctx)}>
                  <Box component="span" sx={mono}>{s.restech}</Box>
                </ValueActions>
              </Field>
              <Field label="Account">
                <ValueActions chip actions={accountActions(s.account, ctx)}>
                  <AccountChip account={s.account} />
                </ValueActions>
              </Field>
            </Stack>
            <Stack direction="row" spacing={3}>
              <Field label="Type">
                <ValueActions actions={typeActions(s.notification_type, ctx)}>
                  <Box component="span" sx={mono}>{s.notification_type}</Box>
                </ValueActions>
              </Field>
              <Field label="Mode">
                <ValueActions chip actions={modeActions(s.mode, ctx)}>
                  <ModeChip mode={s.mode} />
                </ValueActions>
              </Field>
              <Field label="Retry on error">{s.retry_on_error ? 'yes' : 'no'}</Field>
            </Stack>

            <Field label="Callback URL">
              <CopyableValue value={s.callback_url} />
              {urlDrifted && (
                <Alert severity="warning" sx={{ mt: 0.5, py: 0 }}>
                  Differs from the expected URL — run domain sync to reconcile.
                </Alert>
              )}
            </Field>
            {urlDrifted && s.callback_url_expected && (
              <Field label="Callback URL expected">
                <CopyableValue value={s.callback_url_expected} />
              </Field>
            )}

            <Stack direction="row" spacing={3}>
              <Field label="Remote ID">
                {s.remote_id ? (
                  <ValueActions actions={remoteIdActions(s.remote_id, ctx)}>
                    <CopyableValue value={s.remote_id} />
                  </ValueActions>
                ) : (
                  '—'
                )}
              </Field>
              <Field label="Created">
                {s.created_ts ? (
                  <ValueActions actions={createdActions(s.created_ts, ctx)}>
                    {formatDate(s.created_ts)}
                  </ValueActions>
                ) : (
                  formatDate(s.created_ts)
                )}
              </Field>
              <Field label="Last received">
                {s.last_received_ts ? (
                  <ValueActions actions={lastReceivedActions(s.last_received_ts, ctx)}>
                    {formatDate(s.last_received_ts)}
                  </ValueActions>
                ) : (
                  formatDate(s.last_received_ts)
                )}
              </Field>
            </Stack>

            {s.last_error && (
              <Field label={`Last error${s.last_error_ts ? ` (${formatDate(s.last_error_ts)})` : ''}`}>
                <ValueActions actions={lastErrorActions(s.last_error_ts, ctx)}>
                  <Typography variant="body2" color="error" sx={{ wordBreak: 'break-word' }}>
                    {s.last_error}
                  </Typography>
                </ValueActions>
              </Field>
            )}

            <Divider />

            <Typography variant="subtitle2">API credential</Typography>
            {s.api_credential ? (
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                <Field label="Name">
                  <ValueActions actions={credentialActions(s.api_credential)}>
                    {s.api_credential.name}
                  </ValueActions>
                </Field>
                <Field label="Key prefix">
                  <ValueActions actions={credentialPrefixActions(s.api_credential.key_prefix)}>
                    <Box component="span" sx={mono}>{s.api_credential.key_prefix}…</Box>
                  </ValueActions>
                </Field>
                <Field label="Fingerprint">
                  <ValueActions actions={credentialFingerprintActions(s.api_credential.fingerprint, ctx)}>
                    <CopyableValue value={s.api_credential.fingerprint} />
                  </ValueActions>
                </Field>
                <Field label="Status">
                  <ValueActions chip actions={credentialStatusActions(s.api_credential.status)}>
                    <Chip size="small" variant="outlined" label={s.api_credential.status} />
                  </ValueActions>
                </Field>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No credential bound.
              </Typography>
            )}

            {s.remote_data != null && (
              <>
                <Divider />
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                    Registration snapshot
                  </Typography>
                  <ValueActions actions={[act.copy(JSON.stringify(s.remote_data, null, 2), 'Copy JSON')]}>
                    <Typography variant="caption" color="primary">
                      copy JSON
                    </Typography>
                  </ValueActions>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                  Vendor response persisted at registration; auth header values redacted.
                </Typography>
                <JsonBlock value={s.remote_data} />
              </>
            )}

            <Divider />

            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                Live vendor state
              </Typography>
              <Tooltip title="Fetch the remote state again">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => void remoteQuery.refetch()}
                    disabled={remoteQuery.isFetching}
                    aria-label="Refresh remote state"
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>

            {remoteQuery.isFetching && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={22} />
              </Box>
            )}
            {!remoteQuery.isFetching && remoteError && (
              <Alert severity="error">
                Vendor API error while fetching the remote state
                {remoteError.status === 502 ? '' : ` (${remoteError.status})`}: {remoteError.message}
              </Alert>
            )}
            {!remoteQuery.isFetching && remote && (
              <>
                {!remote.exists ? (
                  <Alert severity="error">
                    The vendor has NO subscription with this id — deliveries cannot arrive. Run
                    domain sync (or retry) to re-register.
                  </Alert>
                ) : (
                  <Stack spacing={1}>
                    {remote.auth_matches === true && (
                      <Alert severity="success" sx={{ py: 0 }}>
                        Callback auth matches the bound credential.
                      </Alert>
                    )}
                    {remote.auth_matches === false && (
                      <Alert severity="error" sx={{ py: 0 }}>
                        Callback auth does NOT match the bound credential — deliveries will be
                        rejected. Run domain sync to repair.
                      </Alert>
                    )}
                    {remote.auth_matches === null && (
                      <Typography variant="caption" color="text.secondary">
                        Auth check unavailable — the vendor does not echo header values.
                      </Typography>
                    )}

                    {(remote.differences?.length ?? 0) > 0 ? (
                      <Table size="small" sx={{ '& td, & th': { px: 1 } }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Field</TableCell>
                            <TableCell>Local</TableCell>
                            <TableCell>Remote</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {remote.differences!.map((d) => (
                            <TableRow key={d.field}>
                              <TableCell sx={{ color: 'error.main', fontWeight: 600 }}>
                                {d.field}
                              </TableCell>
                              <TableCell sx={mono}>{d.local ?? '—'}</TableCell>
                              <TableCell sx={{ ...mono, color: 'error.main' }}>
                                {d.remote ?? '—'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No differences — the vendor state matches the local row.
                      </Typography>
                    )}

                    {remote.remote != null && (
                      <>
                        <ValueActions
                          actions={[act.copy(JSON.stringify(remote.remote, null, 2), 'Copy JSON')]}
                        >
                          <Typography variant="caption" color="primary">
                            copy JSON
                          </Typography>
                        </ValueActions>
                        <JsonBlock value={remote.remote} />
                      </>
                    )}
                  </Stack>
                )}
              </>
            )}

            {legacySupported && (
              <>
                <Divider />
                <LegacyWebhooksSection
                  vendor={vendor}
                  state={legacyQuery.data}
                  isFetching={legacyQuery.isFetching}
                  error={legacyQuery.error}
                  onRefresh={() => void legacyQuery.refetch()}
                />
              </>
            )}
            {legacyOutcome && (
              <LegacyWebhookOutcome
                severity={legacyOutcome.severity}
                message={legacyOutcome.message}
                result={legacyOutcome.result}
                onClose={() => setLegacyOutcome(null)}
              />
            )}

            {s.reconcile && (
              <>
                <Divider />
                <ReconcileFlagSection
                  // remount on the saved value so the draft resets after a save
                  key={`${s.reconcile.flag}:${String(s.reconcile.enabled)}`}
                  subscriptionId={subscriptionId}
                  notificationType={s.notification_type}
                  account={s.account}
                  state={s.reconcile}
                  canManage={canManage}
                  onSaved={invalidateDetail}
                />
              </>
            )}

            {canManage && (
              <>
                <Divider />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {canRetry && (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<ReplayIcon />}
                      onClick={() => retryMutation.mutate()}
                      disabled={retryMutation.isPending}
                    >
                      {retryMutation.isPending ? 'Retrying…' : 'Retry registration'}
                    </Button>
                  )}
                  {canToggleEnabled && (
                    <Button
                      size="small"
                      variant="outlined"
                      color={s.status === 'active' ? 'warning' : 'success'}
                      onClick={() => updateMutation.mutate({ disabled: s.status === 'active' })}
                      disabled={updateMutation.isPending}
                    >
                      {s.status === 'active' ? 'Disable' : 'Enable'}
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={onModeClick}
                    disabled={modeBusy || legacyLoading}
                  >
                    {switchMutation.isPending ? 'Switching…' : modeLabel}
                  </Button>
                  {canDisableWebhooks && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      onClick={() => setLegacyAction('disable')}
                      disabled={modeBusy}
                    >
                      {disableWebhooksMutation.isPending ? 'Disabling…' : 'Disable webhooks'}
                    </Button>
                  )}
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={() => setConfirmDelete(true)}
                    disabled={deleteMutation.isPending}
                  >
                    Delete
                  </Button>
                </Stack>
              </>
            )}

            {hasPerm('console_audit_view') && (
              <>
                <Divider />
                <Typography variant="subtitle2">Change history</Typography>
                <HistoryPanel
                  contentType="demo.integration_notification_subscription"
                  objectPk={String(subscriptionId)}
                  onRestored={() => {
                    void detailQuery.refetch();
                    onChanged?.();
                  }}
                />
              </>
            )}
          </Stack>
        )}
      </Box>

      <ConfirmDialog
        open={confirmProcess}
        title="Switch to process mode?"
        message="Deliveries on this subscription will feed the import pipeline from the next notification on — bookings, orders, products or availability update as they arrive."
        confirmLabel="Switch to process"
        onConfirm={() => {
          setConfirmProcess(false);
          updateMutation.mutate({ mode: 'process' });
        }}
        onCancel={() => setConfirmProcess(false)}
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this subscription?"
        message="The vendor-side subscription is deleted first, then the row and its API credential. If the vendor delete fails the row is kept as delete_failed — retry the delete later."
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          setConfirmDelete(false);
          deleteMutation.mutate();
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </Drawer>
  );

  // The dialog is a SIBLING of the Drawer, not a child: a Dialog inside the
  // Drawer's portal is aria-hidden by MUI's ModalManager (lessons.md).
  return (
    <>
      {drawer}
      <LegacyWebhookSwitchDialog
        action={legacyAction}
        vendor={vendor}
        webhooks={legacyAction === 'log_only' ? legacy.disabled : legacy.disableable}
        kept={legacy.unrestorable}
        onConfirm={(action) => {
          setLegacyAction(null);
          setLegacyOutcome(null);
          if (action === 'disable') disableWebhooksMutation.mutate();
          else switchMutation.mutate(action);
        }}
        onCancel={() => setLegacyAction(null)}
      />
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" component="div">
        {children}
      </Typography>
    </Box>
  );
}

function CopyableValue({ value }: { value: string }) {
  const snackbar = useSnackbar();
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Box component="code" sx={mono}>{value}</Box>
      <Tooltip title="Copy">
        <IconButton
          size="small"
          onClick={() => {
            void navigator.clipboard?.writeText(value);
            snackbar.show('Copied.', 'success');
          }}
        >
          <ContentCopyIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

function JsonBlock({ value }: { value: Record<string, unknown> }) {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1,
        bgcolor: 'action.hover',
        borderRadius: 1,
        fontSize: 12,
        fontFamily: 'monospace',
        overflow: 'auto',
        maxHeight: 260,
      }}
    >
      {JSON.stringify(value, null, 2)}
    </Box>
  );
}
